import { HttpErrorResponse } from '@angular/common/http';
import { Component, ElementRef, EventEmitter, Input, OnInit, Output, QueryList, ViewChild, ViewChildren } from '@angular/core';
import { FormArray, FormBuilder, FormGroup, Validators } from '@angular/forms';
import { NgbActiveModal, NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { ToastrService } from 'ngx-toastr';
import { RegExp } from 'src/app/core/config/regex';
import { Context } from 'src/app/shared/models/context';
import { Course, CourseSlide } from 'src/app/shared/models/course';
import { Message } from 'src/app/shared/models/domain';
import { CommonService } from 'src/app/shared/services/common.service';
import { CourseService } from 'src/app/shared/services/course.service';
import { ViewSlidePopupComponent } from '../view-slide-popup/view-slide-popup.component';
@Component({
  selector: 'app-Course-crud',
  templateUrl: './Course-crud.component.html',
  styleUrls: ['./Course-crud.component.scss']
})
export class CourseCrudComponent implements OnInit {

  @ViewChildren('fileInput') fileInputs!: QueryList<ElementRef>;
  @ViewChild('slideImg') slideImg!: ElementRef;
  @Input() course: Course;
  @Input() context: Context;
  @Input() courses: Course[] = [];
  @Output() passEntry: EventEmitter<any> = new EventEmitter();
  public courseSlideForm: FormGroup;
  public courseForm: FormGroup;
  public submitted: boolean = false;
  private modified: boolean = false;
  public statuses: string[] = [];

  constructor(
    private fb: FormBuilder,
    public activeModal: NgbActiveModal,
    private commonService: CommonService,
    private courseService: CourseService,
    private toaster: ToastrService,
    private modalService: NgbModal) {
  }

  ngOnInit(): void {
    this.setCourseForm(this.course);
    this.getCourseStatuses();
    this.getCourseSlides(this.course?.id);
    this.setCourseSlides([]);
  }

  public setCourseForm(course?: Course) {
    if (!course) {
      course = {
        id: null,
        name: '',
        code: '',
        label: '',
        duration: null,
        credits: 0,
        fee: 0,
        questionCount: null,
        hasTest: true,
        maxRetries: 0,
        retryDays: 0,
        recertificationRequired: true,
        recertifyPeriod: 0,
        disabled: false,
        free: true,
        courseStatus: "ACTIVE",
        companyId: this.context?.companyId,
        companyName: '',
        isDeleteInProgress: false,
        isUpdateInProgress: false
      } as Course;
    }

    this.courseForm = this.fb.group({
      id: this.fb.control(course.id),
      name: this.fb.control(course.name, [Validators.required, Validators.pattern(RegExp.name)]),
      code: this.fb.control(course.code?.toUpperCase() || '', [Validators.required, Validators.pattern(/^(?=.*[A-Za-z])(?=.*\d)[A-Za-z\d-]+$/)]),
      label: this.fb.control(course.label, Validators.pattern(RegExp.label)),
      duration: this.fb.control(course.duration, [Validators.required, Validators.min(1)]),
      credits: this.fb.control(course.credits, [Validators.required, Validators.min(0)]),
      fee: this.fb.control(course.fee, [Validators.required, Validators.min(0)]),
      questionCount: this.fb.control(course.questionCount, [Validators.min(0)]),
      hasTest: this.fb.control(course.hasTest),
      free: this.fb.control(course.free),
      maxRetries: this.fb.control(course.maxRetries, [Validators.min(0)]),
      retryDays: this.fb.control(course.retryDays, [Validators.min(0)]),
      recertificationRequired: this.fb.control(course.recertificationRequired),
      recertifyPeriod: this.fb.control(course.recertifyPeriod, [Validators.min(0)]),
      disabled: this.fb.control(course.disabled),
      courseStatus: this.fb.control(course.courseStatus, [Validators.required]),
      companyId: this.fb.control(course.companyId, []),
      companyName: this.fb.control(course.companyName),
    });
    this.courseForm.get('free')?.valueChanges.subscribe(free => {
      if (free) {
        this.courseForm.get('fee')?.setValue(0);
      }
    });
    this.courseForm.get('hasTest')?.valueChanges.subscribe(hasTest => {
      if (!hasTest) {
        this.courseForm.get('retryDays')?.setValue(0);
        this.courseForm.get('maxRetries')?.setValue(0);
      }
    });
    this.courseForm.get('recertificationRequired')?.valueChanges.subscribe(recertificationRequired => {
      if (!recertificationRequired) {
        this.courseForm.get('recertifyPeriod')?.setValue(0);
      }
    });
    this.courseForm.valueChanges.subscribe(value => this.modified = true);
  }

  get f() {
    return this.courseForm.controls;
  }

  get p() {
    return this.courseSlideForm.controls;
  }

  public saveCourse(inProgress?: any) {
    this.submitted = true;
    if (this.courseForm.valid) {
      const course = this.courseForm.value as Course;
      if (!this.modified) {
        this.activeModal.close(false);
        this.commonService.setToastr(200, `course  has been updated successfully!`);
        return;
      }
      inProgress.value = true;
      if (course.id) {
        this.courseService.updateCourse(course.id, course).subscribe({
          next: (response: Message) => {
            if (!response.failed) {
              this.f.id.setValue(course.id)
              this.passEntry.emit(response);
              this.toaster.success(response.text);
              inProgress.value = false;
            } else {
              inProgress.value = false;
              this.commonService.setToastr(500, response.text);
            }
            this.submitted = false;
          },
          error: (error: HttpErrorResponse) => {
            inProgress.value = false;
          },
        });
      } else {
        this.courseService.createCourse(course).subscribe({
          next: (response: Message) => {
            if (!response.failed) {
              const courseId = parseInt(response.payload);
              this.f.id.setValue(courseId);
              this.courseForm.get("id").setValue(courseId);
              this.passEntry.emit(response);
              this.toaster.success(response.text);
              inProgress.value = false;
            } else {
              inProgress.value = false;
              this.commonService.setToastr(500, response.text);
            }
            this.submitted = false;
          },
          error: (error: HttpErrorResponse) => {
            inProgress.value = false;
          },
        });
      }
    }
  }

  private getCourseStatuses() {
    this.courseService.getCourseStatus().subscribe((statuses) => {
      this.statuses = statuses;
    });
  }

  get courseSlides(): FormArray {
    return this.courseSlideForm.get("courseSlides") as FormArray;
  }

  private setCourseSlides(courseSlides: CourseSlide[]) {
    this.courseSlideForm = this.fb.group({
      courseSlides: this.fb.array([])
    });
    if (courseSlides.length > 0) {
      courseSlides.forEach(slide => this.addCourseSlide(slide));
    }
    this.addCourseSlide();
  }

  private addCourseSlide(courseSlide?: CourseSlide) {
    this.courseSlides.push(this.fb.group({
      id: this.fb.control(courseSlide?.id || null),
      courseId: this.fb.control(courseSlide?.courseId || this.f.id.value,),
      title: this.fb.control(courseSlide?.title || "", [Validators.required, Validators.pattern(RegExp.label)]),
      content: this.fb.control(courseSlide?.content || "", [Validators.required, Validators.pattern(RegExp.content)]),
      sequence: this.fb.control(courseSlide?.sequence || this.courseSlides?.value?.length + 1, [Validators.required, Validators.min(1)]),
      mediaUrl: this.fb.control(courseSlide?.mediaUrl || "", []),
      active: this.fb.control(courseSlide?.active || true),
      disabled: this.fb.control(courseSlide?.disabled || false),
      media: this.fb.control(new FormData())
    }));
  }

  public upsertCourseSlide(courseSlide: any, index: number) {
    let errors: string[] = [];
    if (!courseSlide.value.title || courseSlide.value.title.trim().length === 0 || courseSlide.value.title.length > 250) {
      errors.push('Title must be between 1 and 250 characters.');
    }
    if (!courseSlide.value.content || courseSlide.value.content.trim().length === 0 || courseSlide.value.content.length > 2000) {
      errors.push('Content must be between 1 and 2000 characters.');
    }
    if (errors.length > 0) {
      this.toaster.error(errors.join(' '), 'Validation Error');
      return;
    }
  
    let slide = courseSlide.value as CourseSlide;
    courseSlide.isUpdateInProgress = true;
    slide.courseId = this.f.id.value;
  
    const existingSlides = this.courseSlides.controls.map(control => control.value);
    const sequenceExists = existingSlides.some((s, i) => i !== index && s.sequence === slide.sequence);
    if (sequenceExists) {
      courseSlide.isUpdateInProgress = false;
      this.toaster.warning(`Sequence number ${slide.sequence} already exists!`);
      return;
    }
  
    if (slide.id) {
      this.courseService.updateCourseSlide(slide.id, slide).subscribe({
        next: (message: Message) => {
          if (!message.failed && message.payload) {
            this.courseSlides.at(index).patchValue({ id: parseInt(message.payload) });
            if (slide.media && slide.media.getAll('file').length > 0) {
              this.uploadCourseSlideImage(message.payload, index, slide, courseSlide);
            } else {
              courseSlide.isUpdateInProgress = false;
              this.checkAndAddSlide();
              this.toaster.success(message.text);
            }
          } else {
            courseSlide.isUpdateInProgress = false;
            this.commonService.setToastr(500, message.text);
          }
        },
        error: (error: HttpErrorResponse) => {
          courseSlide.isUpdateInProgress = false;
        }
      });
    } else {
      this.courseService.createCourseSlide(slide).subscribe({
        next: (message: Message) => {
          if (!message.failed && message.payload) {
            this.courseSlides.at(index).patchValue({ id: parseInt(message.payload) });
            if (slide.media && slide.media.getAll('file').length > 0) {
              this.uploadCourseSlideImage(message.payload, index, slide, courseSlide);
            } else {
              courseSlide.isUpdateInProgress = false;
              this.checkAndAddSlide();
              this.toaster.success(message.text);
            }
          } else {
            courseSlide.isUpdateInProgress = false;
            this.commonService.setToastr(500, message.text);
          }
        },
        error: (error: HttpErrorResponse) => {
          courseSlide.isUpdateInProgress = false;
        }
      });
    }
  }
  

  private checkAndAddSlide() {
    const lastSlide: CourseSlide = this.courseSlides.at(this.courseSlides.value.length - 1).value;
    if (lastSlide?.id && lastSlide?.title && lastSlide.content && lastSlide.sequence) this.addCourseSlide();
  }

  private uploadCourseSlideImage(slideId: number, index: number, slide: CourseSlide, courseSlide: any): void {
    this.courseService.upsertCourseSlideImage(slideId, slide.media).subscribe({
      next: (resp: Message) => {
        courseSlide.isUpdateInProgress = false;
        if (!resp.failed) {
          const uploadedMediaUrl = resp.payload;
          this.courseSlides.at(index).patchValue({ mediaUrl: uploadedMediaUrl });
          slide.mediaUrl = uploadedMediaUrl;
          this.checkAndAddSlide();
          slide.media = new FormData();
          this.toaster.success(resp.text);
        } else {
          this.toaster.error(resp.text);
          this.checkAndAddSlide();
          slide.media = new FormData();
        }
      },
      error: (err) => {
        courseSlide.isUpdateInProgress = false;
        if (err.status === 413) {
          this.toaster.error("File size exceeds the allowed limit. Please upload a smaller file.");
        } else {
          this.toaster.error("Image upload failed. Please try again.");
        }
        this.checkAndAddSlide();
        slide.media = new FormData();
      }
    });
  }

  public deleteCourseSlide(CourseSlide: any, i: number) {
    if (CourseSlide.valid) {
      const courseSlideValue = CourseSlide.value as CourseSlide;
      courseSlideValue.courseId = this.f.id.value;
      if (courseSlideValue.id) {
        CourseSlide.isDeleteInProgress = true;
        this.courseService.deleteCourseSlide(courseSlideValue.id).subscribe({
          next: (message: Message) => {
            if (!message.failed) {
              this.courseSlides.removeAt(i);
              this.commonService.setToastr(200, message.text);
              CourseSlide.isDeleteInProgress = false;
            }
          },
          error: (error: HttpErrorResponse) => {
            CourseSlide.isDeleteInProgress = false;
          }
        });
      } else {
        this.courseSlides.removeAt(i);
        if (this.courseSlides.length < 1) {
          this.addCourseSlide();
        }
      }
    }
  }

  public triggerFileInput(index: number): void {
    const fileInputArray = this.fileInputs.toArray();
    if (fileInputArray[index]) {
      fileInputArray[index].nativeElement.click();
    } else {
    }
  }

  async onFileSelected(event: Event, index: number, slide: CourseSlide): Promise<void> {
    const input = event.target as HTMLInputElement;
    if (input.files && input.files.length > 0) {
      const file = input.files[0];
      slide.media = new FormData();
      slide.media.append('file', file);
      slide.fileName = file.name;
    }
  }

  public previewImage(index: number) {
    const modalRef = this.modalService.open(ViewSlidePopupComponent, { size: 'xl', backdrop: false });
    modalRef.componentInstance.slide = { ...this.courseSlides.at(index).value };
    modalRef.componentInstance.emitResponse.subscribe((slide: CourseSlide) => {
      if (slide?.id) {
        this.courseSlides.at(index).patchValue({ mediaUrl: slide.mediaUrl });
      }
    });
  }

  private getCourseSlides(courseId: number): void {
    if (courseId) {
      this.courseService.getCourseSlides(courseId).subscribe(courseSlides => {
        const sortedSlides = courseSlides.sort((a, b) => a.id - b.id);
        this.setCourseSlides(sortedSlides);
      });
    }
  }

}